import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Toast state that survives a route change. When an action (create, duplicate,
// delete) navigates to another page, the source unmounts before its own toast
// can render — so the message is handed to the destination via navigation state
// and popped here on arrival.
//
// Returns the same { toast, showToast, hideToast } shape as an inline toast,
// plus navigateWithToast(to, message, variant) for the cross-page case.
export const useFlashToast = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [toast, setToast] = useState({
    show: false,
    variant: "success",
    message: "",
  });

  // Stable identities so callers can list these in effect deps without
  // re-running on every render.
  const showToast = useCallback(
    (message, variant = "success") =>
      setToast({ show: true, message, variant }),
    []
  );
  const hideToast = useCallback(
    () => setToast((t) => ({ ...t, show: false })),
    []
  );
  const navigateWithToast = useCallback(
    (to, message, variant = "success") =>
      navigate(to, { state: { flash: { message, variant } } }),
    [navigate]
  );

  // Pop a toast handed to us via navigation state, then clear the state so a
  // refresh or back-navigation doesn't replay it.
  useEffect(() => {
    const flash = location.state?.flash;
    if (!flash?.message) return;
    showToast(flash.message, flash.variant);
    navigate(location.pathname + location.search, {
      replace: true,
      state: null,
    });
  }, [location, navigate, showToast]);

  return { toast, showToast, hideToast, navigateWithToast };
};
